/**
 * Widget: Recent Team Stories
 * Shows latest team success stories
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Heart, MessageCircle, ArrowRight } from 'lucide-react';
import { mockFrameworkStories } from '../../data/mockTeamData';
import type { FrameworkStory } from '../../types/collaboration';

interface RecentTeamStoriesProps {
  maxDisplay?: number;
  compact?: boolean;
}

export const RecentTeamStories: React.FC<RecentTeamStoriesProps> = ({
  maxDisplay = 3,
  compact = false,
}) => {
  const recentStories = mockFrameworkStories
    .sort((a: FrameworkStory, b: FrameworkStory) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, maxDisplay);

  if (compact) {
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold text-charcoal-900 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-charcoal-600" />
            Recent Team Stories
          </h3>
          <Link
            to="/elevation/team"
            className="text-xs text-primary-600 hover:text-primary-800 font-semibold"
          >
            View All →
          </Link>
        </div>
        <div className="space-y-3">
          {recentStories.map((story: FrameworkStory) => (
            <Link
              key={story.id}
              to="/elevation/team"
              className="block p-3 bg-charcoal-50 border border-charcoal-200 hover:bg-charcoal-100 transition-colors group"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-charcoal-300 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {story.author.name
                    .split(' ')
                    .map((n: string) => n[0])
                    .join('')
                    .toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-charcoal-900 line-clamp-1 group-hover:text-primary-600">
                    {story.title}
                  </div>
                  <div className="text-xs text-charcoal-600 mt-0.5">
                    {story.author.name}
                  </div>
                  <div className="text-xs text-charcoal-500 mt-0.5">
                    {story.frameworkName}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-4 text-center">
          <Link
            to="/elevation/team"
            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            View all team stories
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-coral-50 to-primary-50 border-2 border-coral-200 overflow-hidden">
      <div className="bg-gradient-to-r from-coral-600 to-primary-600 p-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          Recent Team Success Stories
        </h3>
        <p className="text-sm text-white/90 mt-1">
          See how your teammates are applying frameworks
        </p>
      </div>

      <div className="p-4 space-y-3">
        {recentStories.map((story: FrameworkStory) => (
          <div
            key={story.id}
            className="bg-[var(--bg-raised)] border border-coral-200 p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-500 to-primary-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {story.author.name
                  .split(' ')
                  .map((n: string) => n[0])
                  .join('')
                  .toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-charcoal-900 mb-1">{story.title}</div>
                <div className="text-xs text-charcoal-600 flex items-center gap-2">
                  <span className="font-semibold">{story.author.name}</span>
                  <span>·</span>
                  <span>{new Date(story.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="mt-1">
                  <Link
                    to={`/elevation/entry/${story.frameworkId}`}
                    className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-semibold"
                  >
                    {story.frameworkName}
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            </div>

            <p className="text-sm text-charcoal-700 mb-3 line-clamp-2">
              {story.situation}
            </p>

            <div className="flex items-center justify-between pt-3 border-t border-charcoal-100">
              <div className="flex items-center gap-3 text-xs text-charcoal-600">
                <button className="flex items-center gap-1 hover:text-coral-600 transition-colors">
                  <Heart className="h-4 w-4" />
                  <span>{story.likes}</span>
                </button>
                <button className="flex items-center gap-1 hover:text-primary-600 transition-colors">
                  <MessageCircle className="h-4 w-4" />
                  <span>{story.commentCount}</span>
                </button>
              </div>
              <Link
                to="/elevation/team"
                className="text-xs text-primary-600 hover:text-primary-800 font-semibold"
              >
                Read More →
              </Link>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 bg-[var(--bg-raised)] border-t border-coral-200">
        <Link
          to="/elevation/team"
          className="flex items-center justify-center gap-2 px-4 py-2 bg-coral-600 hover:bg-coral-700 text-white font-semibold transition-colors"
        >
          View All Team Stories
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

